import { Dropdown, Grid, Tag, Typography } from "antd";
import { FontAwesomeIcon, Icon, SvgIcon } from "assets/icons";
import { FilterLoadMore } from "components/CustomFilterMenu";
import CustomListFileImage from "components/CustomListFileImage";
import CustomTextHiddenLine from "components/CustomTextHiddenLine";
import { STATUS_ORDER, STOCK_STATUS } from "config/constants";
import { CARRIER_METHOD_LABEL, ORDER_TYPE, SHIPPING_TYPE } from "features/Cart/constants";
import { PAYMENT_STATUS } from "features/Order/constants";
import DeliveryDeadline from "features/Order/Detail/components/DeliveryDeadline";
import {
  buildCustomerListUrl,
  buildSellerApprovalSearchUrl,
  isWaitingSellerApproval,
} from "features/PotentialCustomer/helper";
import { useGetCreatedBy, useGetSalesmen, useGetWarehouses, useOrderPermissions } from "hooks/order";
import { useCustomerPermissions } from "hooks/potentialCustomer/query";
import { useSalesChannelInputOptions } from "hooks/salesChannel";
import { useSellerPermissions } from "hooks/seller";
import { t } from "i18next";
import { isEqual } from "lodash";
import moment from "moment";
import { SELLER_APPROVAL_STATUS } from "pages/App/Agent/constants";
import {
  renderColorTagByPaymentStatus,
  renderOrderPriority,
  renderPaymentMethod,
  renderRemainingDeliverTime,
  renderStatus,
  renderStockStatus,
  renderStringOrderPromotionType,
  renderStringTagByPaymentStatus,
  renderVATAndFullStockRequired,
  tagColorSalesChannel,
} from "pages/App/Order/conts";
import { useOrder } from "pages/App/Order/hooks";
import { useState } from "react";
import { Link } from "react-router-dom";
import { buildQueryString, formatCurrency, getQuery } from "utils/helperFuncs";
import FilterCancelReason from "../FilterCancelReason";
import FilterOrder from "../FilterOrder";
import SuccessDeliveryButton from "../SuccessDeliveryButton";

export const inStockOption = [
  { value: STOCK_STATUS.FULL, label: t("common.full") },
  { value: STOCK_STATUS.LACK, label: t("common.lack") },
  { value: STOCK_STATUS.OUT_OF_STOCK, label: t("common.outOfStock") },
];

export const promotionTypeOption = [
  { value: "NO_PROMOTION", label: t("Không có ưu đãi") },
  // { value: "PROMOTION", label: t("Khuyến mãi 01 sản phẩm") },
  // { value: "PROMOTION", label: t("Khuyến mãi combo") },
  // { value: "PROMOTION", label: t("Khuyến mãi đơn hàng") },
  { value: "GROUP_BUYING_CAMPAIGN", label: t("Khuyến mãi mua chung") },
  { value: "PRE_ORDER_CAMPAIGN", label: t("Khuyến mãi đặt trước") },
  { value: "PRE_ORDER_POLICY", label: t("Đặt trước") },
  // { value: "AUCTION", label: t("Đấu giá") },
  // { value: "", label: t("Ưu đãi kết hợp") },
  // { value: "", label: t("Khác") },
];

export const paymentStatusOption = [
  { value: PAYMENT_STATUS.PENDING, label: t("bank_transfer.order.pending") },
  { value: PAYMENT_STATUS.WAITING_APPROVAL, label: t("bank_transfer.order.waiting_approval") },
  { value: PAYMENT_STATUS.PAID, label: t("bank_transfer.order.paid") },
];

const useColumnsOrder = (params, setParams, filterTags, setFilterTags) => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const [paramCreatedBy, setParamCreatedBy] = useState({
    pagination: { offset: 0, limit: 10 },
    filters: { query: "" },
  });
  const [paramSalesman, setParamSalesman] = useState({
    pagination: { offset: 0, limit: 10 },
    filters: { query: "" },
  });
  const { data: warehousesOptions } = useGetWarehouses();
  const { data: createdByOptions, fetchMore } = useGetCreatedBy(paramCreatedBy);
  const {
    data: salesmenOptions,
    loadMoreSalesmen,
    hasMoreData: hasMoreSalesmen,
    loading: loadingSalesmen,
  } = useGetSalesmen(paramSalesman);

  // Latch một chiều: chỉ gọi `getShippingTypesV2` từ lần đầu người dùng mở dropdown filter PTVC,
  // và giữ `true` để những lần mở sau không phải chờ load lại.
  const [shippingFilterOpened, setShippingFilterOpened] = useState(false);

  const {
    orderPriorityOptions,
    shippingOptions,
    paymentMethodOptions,
    statusOptions,
    fullStockRequiredOption,
    vatOption,
  } = useOrder({ enableShippingTypes: shippingFilterOpened });

  const { canCompleteOrder } = useOrderPermissions();

  // /potential-customer-list cần CUSTOMERS_VIEW; /seller và /seller/detail/:id cần SELLER_VIEW
  // (route detail trả Page403). Thiếu quyền thì để nguyên text, tránh dẫn người dùng tới màn bị chặn.
  const { canCustomerView } = useCustomerPermissions();
  const { canView: canSellerView } = useSellerPermissions();

  const { convertedData: dataSalesChannel } = useSalesChannelInputOptions();

  const GET_QUERY = getQuery();

  const onFilter = (value) => {
    const newParams = {
      ...params,
      filters: { ...params.filters, ...value },
      pagination: {
        offset: 0,
        limit: 10,
      },
    };
    setParams(newParams);
    buildQueryString({
      params: {
        ...newParams.filters,
        ...newParams.pagination,
        activeKey: GET_QUERY?.activeKey,
      },
    });
  };

  const loadMoreDataCreatedBy = async () => {
    await fetchMore({
      variables: {
        pagination: { offset: createdByOptions.length, limit: 10 },
      },
      updateQuery: (pv, { fetchMoreResult }) => {
        const isSamePaginationData = isEqual(
          pv.user.pagination.paginationData,
          fetchMoreResult.user.pagination.paginationData
        );
        if (isSamePaginationData || fetchMoreResult?.user?.pagination?.users.length === 0) return pv;
        const newData = {
          user: {
            pagination: {
              paginationData: { ...fetchMoreResult?.user?.pagination?.paginationData },
              users: [...pv?.user?.pagination?.users, ...fetchMoreResult?.user?.pagination?.users],
            },
          },
        };
        return newData;
      },
    });
  };

  const onSearchTextChange = (values) => {
    setParamCreatedBy({
      ...paramCreatedBy,
      pagination: { offset: 0, limit: 10 },
      filters: { query: values },
    });
  };

  const onSearchSalesmen = (values) => {
    setParamSalesman({
      ...paramSalesman,
      pagination: { offset: 0, limit: 10 },
      filters: { query: values },
    });
  };

  const renderDropdownFilter = (
    options,
    keyObj,
    typeSelection = "checkbox",
    params,
    setParams,
    paginationData = undefined,
    onVisibleChange = undefined
  ) => {
    return (
      <Dropdown
        overlayClassName="dropdown-overlay"
        trigger="click"
        onVisibleChange={onVisibleChange}
        overlay={
          <FilterOrder
            options={options}
            keyObj={keyObj}
            onFilter={onFilter}
            pagination={paginationData}
            param={params}
            setParam={setParams}
            filterTags={filterTags}
            setFilterTags={setFilterTags}
            typeSelection={typeSelection}
          />
        }
        placement="bottomRight"
      >
        <FontAwesomeIcon icon={Icon.faFilter} />
      </Dropdown>
    );
  };

  const handleSort = (by) => {
    //check sort have orderType is ASC or DESC
    if (params.sort[0]?.orderType === "ASC") {
      const newParams = {
        ...params,
        sort: [{ by: by, orderType: "DESC" }],
      };
      setParams(newParams);
    } else {
      const newParams = {
        ...params,
        sort: [{ by: by, orderType: "ASC" }],
      };
      setParams(newParams);
    }
  };

  const all = {
    //STT (fixed column)
    index: {
      fixed: screens.lg ? "left" : undefined,
      title: <Typography.Text>{t("order.orderList.index")}</Typography.Text>,
      render: (text, record, index) => index + 1,
      width: 50,
      align: "center",
    },
    //Ngày tạo (fixed column)
    createdAt: {
      fixed: screens.lg ? "left" : undefined,
      title: <div className="title-table">{t("order.orderList.createdAt")}</div>,
      dataIndex: "createdAt",
      width: 110,
      sorter: true,
    },
    //Loại đơn (fixed column)
    orderType: {
      fixed: screens.lg ? "left" : undefined,
      title: (
        <div className="title-table">
          Loại đơn
          {renderDropdownFilter(promotionTypeOption, "promotionTypes", "checkbox")}
        </div>
      ),

      render: (text, record, index) => {
        return renderStringOrderPromotionType(record?.promotionType);
      },
      width: 180,
    },
    //Mã đơn (fixed column)
    orderCode: {
      fixed: screens.lg ? "left" : undefined,
      title: <Typography.Text>{t("order.orderList.orderCode")}</Typography.Text>,
      dataIndex: "number",
      render: (text, record) => {
        return (
          <Link to={`/order/${record?.id}`}>
            <b>{text}</b>
          </Link>
        );
      },
      width: 180,
    },
    //GIÁ TRỊ ĐƠN
    subTotal: {
      title: (
        <div className="title-table">
          {t("order.orderList.totalOrder")}
          <SvgIcon.SortIcon onClick={() => handleSort("GRAND_TOTAL")} />
        </div>
      ),
      align: "end",
      width: 130,
      render: (text, record) => {
        const totalOrder = record?.orderType !== ORDER_TYPE.CUSTOMER ? record?.total : record.customerTotal;
        return formatCurrency(totalOrder);
      },
    },
    //KHO XUẤT
    exportedWarehouse: {
      title: (
        <div className="title-table">
          {t("order.orderList.exportWarehouse")}
          {renderDropdownFilter(warehousesOptions, "exportedWarehouseIDs", "checkbox")}
        </div>
      ),
      dataIndex: "exportedWarehouse",
      width: 110,
    },
    //KHÁCH HÀNG
    receiverName: {
      title: (
        <div className="title-table">
          {t("order.orderList.receiverName")}
          {/* <FontAwesomeIcon icon={Icon.faFilter} /> */}
        </div>
      ),
      // dataIndex: "receiverName",
      width: 400,
      render: (_, record) => {
        const seller = record?.seller;
        const isApprovedSeller = seller?.approvalStatus === SELLER_APPROVAL_STATUS.APPROVED;

        // Chưa duyệt thì sang màn danh sách chứa họ (không có route chi tiết khách hàng riêng —
        // chi tiết là Drawer trong màn danh sách). Hai màn danh sách nằm sau hai quyền khác nhau.
        const canOpenList = isWaitingSellerApproval(record?.potentialCustomer) ? canSellerView : canCustomerView;
        const customerListUrl = !isApprovedSeller && canOpenList && buildCustomerListUrl(record?.potentialCustomer);

        // Không có `potentialCustomer` thì vẫn sang được tab Chờ duyệt, chỉ là lọc bằng tìm kiếm
        // thay vì bằng ID. Hết cách mới về Chi tiết đại lý — luôn có `seller.id` nên luôn đi được.
        // Cả /seller lẫn /seller/detail/:id đều chặn bằng SELLER_VIEW, thiếu quyền thì để text.
        const detailUrl =
          customerListUrl ||
          (canSellerView &&
            ((!isApprovedSeller && buildSellerApprovalSearchUrl(seller)) ||
              (seller?.id && `/seller/detail/${seller?.id}`)));

        const content = (
          <>
            <b>{seller?.code}</b>
            <CustomTextHiddenLine text={seller?.fullName} line={2} />
          </>
        );

        return (
          <div>
            {detailUrl ? (
              <Link to={detailUrl} target="_blank">
                {content}
              </Link>
            ) : (
              content
            )}
          </div>
        );
      },
    },
    //ĐỘ ƯU TIÊN
    orderPriority: {
      title: (
        <div className="title-table">
          {t("order.orderList.orderPriority")}
          {renderDropdownFilter(orderPriorityOptions, "priorities", "checkbox")}
        </div>
      ),
      dataIndex: "orderPriority",
      render: (text) => {
        return renderOrderPriority(text);
      },
      width: 130,
    },
    //Phương thức giao hàng
    shippingType: {
      title: (
        <div className="title-table">
          Phương thức giao hàng
          {renderDropdownFilter(shippingOptions, "shippingTypes", "checkbox", undefined, undefined, undefined, () =>
            setShippingFilterOpened(true)
          )}
        </div>
      ),
      // Tên PTVC lấy thẳng `order.shippingTypeName` của BE — đổi tên trong DB là cột đổi theo, và
      // PTVC mới (DELIVER_3PARTY_INSTANT, URBAN_NOT_COD) không còn bị bỏ trắng như khi map cứng
      // theo enum ở FE.
      dataIndex: "shippingTypeName",
      render: (text) => text || "--",
      width: 200,
    },
    //PHƯƠNG THỨC THANH TOÁN
    paymentMethod: {
      title: (
        <div className="title-table">
          {t("order.orderList.paymentMethod")}
          {renderDropdownFilter(paymentMethodOptions, "paymentMethods", "checkbox")}
        </div>
      ),
      dataIndex: "paymentMethod",
      render: (text) => {
        return renderPaymentMethod(text);
      },
      width: 150,
    },
    //Trạng thái thanh toán
    paymentStatus: {
      title: (
        <div className="title-table">
          {t("order.orderList.statusPayment")}
          {renderDropdownFilter(paymentStatusOption, "paymentStatuses", "checkbox")}
        </div>
      ),
      dataIndex: "paymentMethod",
      render: (_, record) => {
        return record?.paymentStatus ? (
          <Tag color={renderColorTagByPaymentStatus(record?.paymentStatus)}>
            {renderStringTagByPaymentStatus(record?.paymentStatus)}
          </Tag>
        ) : (
          "- -"
        );
      },
      width: 200,
    },
    //Trạng thái đơn hàng
    status: {
      title: (
        <div className="title-table">
          Trạng thái đơn hàng
          {renderDropdownFilter(statusOptions, "statuses", "checkbox")}
        </div>
      ),
      dataIndex: "status",
      render: (text) => {
        return renderStatus(text);
      },
      width: 150,
    },
    salesPerson: {
      title: (
        <div className="title-table">
          {t("seller.salesMan")}
          <Dropdown
            trigger="click"
            overlay={
              <FilterLoadMore
                onFilter={(values) => onFilter({ salesmanID: values[0] })}
                data={salesmenOptions}
                onLoadMoreData={loadMoreSalesmen}
                onSearchTextChange={onSearchSalesmen}
                hasMore={hasMoreSalesmen}
                loading={loadingSalesmen}
              />
            }
            placement="bottomRight"
          >
            <FontAwesomeIcon icon={Icon.faFilter} />
          </Dropdown>
        </div>
      ),
      width: 150,
      render: (_, record) => {
        return record?.salesman?.fullname ? (
          <CustomTextHiddenLine line={2} text={record?.salesman?.fullname} />
        ) : (
          <Typography.Text style={{ color: "#9CADC4" }}>--</Typography.Text>
        );
      },
    },
    salesChannel: {
      fixed: screens.lg ? "right" : undefined,
      title: (
        <div className="title-table">
          {t("order.orderDetail.saleChannel")}
          {renderDropdownFilter(dataSalesChannel, "saleChannelIDs", "checkbox")}
        </div>
      ),
      dataIndex: "saleChannel",
      render: (_, record) => {
        return <Tag color={tagColorSalesChannel?.[record?.saleChannel?.type]}>{record?.saleChannel?.name}</Tag>;
      },
      width: 150,
    },
    //Người tạo (fixed column)
    createdByStatus: {
      fixed: screens.lg ? "right" : undefined,
      title: (
        <div className="title-table">
          {t("order.orderList.createdBy")}
          <Dropdown
            trigger="click"
            overlay={
              <FilterLoadMore
                onFilter={(values) => onFilter({ createdByIDs: values })}
                data={createdByOptions}
                onLoadMoreData={loadMoreDataCreatedBy}
                onSearchTextChange={onSearchTextChange}
              />
            }
            placement="bottomRight"
          >
            <FontAwesomeIcon icon={Icon.faFilter} />
          </Dropdown>
        </div>
      ),
      dataIndex: "createdBy",
      width: 200,
      align: "center",
      render: (_, record) => {
        return (
          <>
            <div>{record?.createdBy}</div>
            {record?.seller?.approvalStatus !== SELLER_APPROVAL_STATUS.APPROVED && (
              <div style={{ fontSize: "12px", color: "#5B6673" }}>(Chờ xác thực)</div>
            )}
          </>
        );
      },
    },
    //TÌNH TRẠNG TỒN
    inStockStatus: {
      title: (
        <div className="title-table">
          {t("order.orderList.inStockStatus")}
          {/* <FontAwesomeIcon icon={Icon.faFilter} /> */}

          {renderDropdownFilter(inStockOption, "stockStatus", "radio")}
        </div>
      ),
      dataIndex: "inStockStatus",
      width: 160,
      render: (text) => renderStockStatus(text),
    },
    //GIAO KHI CHƯA ĐỦ HÀNG
    fullStockRequired: {
      title: (
        <div className="title-table">
          {t("order.orderList.fullStockRequired")}
          {/* <FontAwesomeIcon icon={Icon.faFilter} /> */}
          {renderDropdownFilter(fullStockRequiredOption, "fullStockRequired", "checkbox")}
        </div>
      ),
      dataIndex: "fullStockRequired",
      width: 150,
      align: "center",
      render: (text) => renderVATAndFullStockRequired(text),
    },
    //XUẤT VAT
    vat: {
      title: (
        <div className="title-table">
          {t("order.orderList.exportVAT")}
          {/* <FontAwesomeIcon icon={Icon.faFilter} /> */}
          {renderDropdownFilter(vatOption, "vat", "checkbox")}
        </div>
      ),
      width: 120,
      dataIndex: "vat",
      align: "center",
      render: (text) => renderVATAndFullStockRequired(text),
    },
    //THỜI GIAN CÒN LẠI
    remainingDeliverTime: {
      title: (
        <div className="title-table">
          {t("order.orderList.remainingDeliverTime")}
          <SvgIcon.SortIcon onClick={() => handleSort("CREATED_AT")} />
        </div>
      ),
      dataIndex: "remainingDeliverTime",
      width: 180,
      align: "center",
      render: (text) => {
        return renderRemainingDeliverTime(text);
      },
    },
    //ĐỊA CHỈ NGƯỜI NHẬN
    receiverContactAddress: {
      title: <Typography.Text>{t("order.orderList.receiverContactAddress")}</Typography.Text>,
      dataIndex: "receiverContactAddress",
      width: 250,
    },
    //Thời gian giao hàng
    deliveryDeadline: {
      title: <Typography.Text>Thời gian giao hàng</Typography.Text>,
      render: (_, record) => {
        return (
          <DeliveryDeadline
            deliveryDeadline={record?.deliveryDeadline}
            expectedDeliveryFrom={record.minDeliveryDate}
            expectedDeliveryTo={record.maxDeliveryDate}
            timeCheck={
              status === "COMPLETED" || status === "CANCELED"
                ? moment(record?.fullEvents[record?.fullEvents.length - 1].createdAt)
                : moment()
            }
            statusCheck={status === "COMPLETED" || status === "CANCELED"}
          />
        );
      },
      width: 200,
      align: "left",
      dataIndex: "deliveryDeadline",
      sorter: true,
    },
    //ĐƠN VỊ VẬN CHUYỂN
    shippingUnit: {
      title: <Typography.Text>{t("order.orderList.shippingUnit")}</Typography.Text>,
      dataIndex: "shippingUnit",
      width: 230,
    },
    //GHI CHÚ NỘI BỘ
    internalNote: {
      title: <Typography.Text>{t("order.orderList.internalNote")}</Typography.Text>,
      dataIndex: "internalNote",
      width: 150,
    },
    //NGÀY GIAO HÀNG THÀNH CÔNG
    successfulDeliveryDate: {
      title: <Typography.Text>{t("order.orderList.successfulDeliveryDate")}</Typography.Text>,
      dataIndex: "successfulDeliveryDate",
      width: 150,
    },
    //SỐ ĐIỆN THOẠI ĐVVC
    shipperTelephone: {
      title: <Typography.Text>{t("order.orderList.shipperTelephone")}</Typography.Text>,
      width: 150,
      dataIndex: "shipperTelephone",
    },
    //ĐỊA ĐIỂM GIAO HÀNG
    senderAddress: {
      title: <Typography.Text>{t("order.orderList.senderAddress")}</Typography.Text>,
      render: (record) => <Typography.Text>{record?.receiverContactAddress}</Typography.Text>,
      width: 300,
    },
    //GHI CHÚ ĐANG GIAO
    noteShipping: {
      title: <Typography.Text>{t("order.orderList.noteShipping")}</Typography.Text>,
      dataIndex: "noteShipping",
      width: 450,
    },
    //SỐ ĐIỆN THOẠI KHÁCH HÀNG
    receiverContactPhone: {
      title: <Typography.Text>{t("order.orderList.receiverContactPhone")}</Typography.Text>,
      dataIndex: "receiverContactPhone",
      width: 150,
    },
    //Giao thành công
    linkShippingSuccess: {
      render: (text, record) => canCompleteOrder && <SuccessDeliveryButton orderID={record.id} />,
      width: 150,
    },
    //GHI CHÚ ĐƠN HÀNG
    note: {
      title: <Typography.Text>{t("order.orderList.note")}</Typography.Text>,
      dataIndex: "note",
      width: 150,
    },
    //LÝ DO HỦY ĐƠN
    cancelReason: {
      title: (
        <Typography.Text>
          {t("order.orderList.cancelReason")}{" "}
          <FilterCancelReason
            keyObj="cancelReasons"
            onFilter={onFilter}
            params={params}
            setParam={setParams}
            filterTags={filterTags}
            setFilterTags={setFilterTags}
          />
        </Typography.Text>
      ),
      width: 250,
      render: (_, record) => {
        return (
          <Typography.Text>
            {t("order.cancelReasons.cancelBy")}{" "}
            {record?.fullEvents?.[record?.fullEvents?.length - 1]?.createdBy?.type === "SELLER"
              ? t("order.cancelReasons.seller")
              : t("order.cancelReasons.staff")}{" "}
            {t("order.cancelReasons.by")}:
            <br />
            {record?.cancelReason}
          </Typography.Text>
        );
      },
    },
    //Thông tin đơn vị vận chuyển
    transportUnitInformation: {
      title: <Typography.Text>Thông tin đơn vị vận chuyển</Typography.Text>,
      width: 200,
      render: (_, record) => {
        const busConfig = record?.shippingConfiguration?.busConfig;
        const deliver3PartyConfig = record?.shippingConfiguration?.deliver3PartyConfig;

        switch (record.shippingType) {
          case SHIPPING_TYPE.PICK_UP_COUNTER:
            return "<Khách đến lấy hàng>";
          // URBAN_NOT_COD đi cùng URBAN_COD: cả hai đều là Hồng Anh tự giao nội thành, thông tin
          // hiển thị là shipper chứ không phải ĐVVC.
          case SHIPPING_TYPE.URBAN_COD:
          case SHIPPING_TYPE.URBAN_NOT_COD:
            return (
              <div>
                {record.shipperName || "--"} - {record.shipperTelephone || "--"}
              </div>
            );
          case SHIPPING_TYPE.BUS:
            return (
              <div>
                <div>
                  {busConfig?.busName || "--"} - {busConfig?.telephone || "--"}
                </div>
                <div>{busConfig?.busStation || "--"}</div>
              </div>
            );
          case SHIPPING_TYPE.DELIVER_3PARTY:
          case SHIPPING_TYPE.DELIVER_3PARTY_INSTANT:
            return CARRIER_METHOD_LABEL[deliver3PartyConfig?.carrierMethod] || "--";
          default:
            // Trước đây default trả "<Hủy đơn trước khi giao giao vận>" — sai nghĩa: `shippingType`
            // là NonNull và không có giá trị nào mang nghĩa "đã huỷ", nên nhánh này chỉ bị chạm khi
            // BE thêm PTVC mà FE chưa biết. PTVC mới rơi vào đây sẽ khiến vận hành tưởng đơn bị huỷ.
            return "--";
        }
      },
    },
    //Ảnh giao hàng
    image: {
      title: <Typography.Text>{t("order.orderList.image")}</Typography.Text>,
      dataIndex: "fileURLs",
      width: 150,
      align: "center",
      render: (_, record, index) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CustomListFileImage listImage={record?.fileURLs} size={80} />
        </div>
      ),
    },
  };

  const {
    //STT (fixed column)
    index,
    //Ngày tạo (fixed column)
    createdAt,
    //Loại đơn (fixed column)
    orderType,
    //Mã đơn (fixed column)
    orderCode,
    //GIÁ TRỊ ĐƠN
    subTotal,
    //KHO XUẤT
    exportedWarehouse,
    //KHÁCH HÀNG
    receiverName,
    //ĐỘ ƯU TIÊN
    orderPriority,
    //Phương thức giao hàng
    shippingType,
    //PHƯƠNG THỨC THANH TOÁN
    paymentMethod,
    //Trạng thái thanh toán
    paymentStatus,
    //Trạng thái đơn hàng
    status,
    //Kênh bán (fixed column)
    salesChannel,
    //Người tạo (fixed column)
    createdByStatus,
    //TÌNH TRẠNG TỒN
    inStockStatus,
    //GIAO KHI CHƯA ĐỦ HÀNG
    fullStockRequired,
    //XUẤT VAT
    vat,
    //THỜI GIAN CÒN LẠI
    remainingDeliverTime,
    //ĐỊA CHỈ NGƯỜI NHẬN
    receiverContactAddress,
    //Thời gian giao hàng
    deliveryDeadline,
    //ĐƠN VỊ VẬN CHUYỂN
    shippingUnit,
    //GHI CHÚ NỘI BỘ
    internalNote,
    //NGÀY GIAO HÀNG THÀNH CÔNG
    successfulDeliveryDate,
    //SỐ ĐIỆN THOẠI ĐVVC
    shipperTelephone,
    //ĐỊA ĐIỂM GIAO HÀNG
    senderAddress,
    //GHI CHÚ ĐANG GIAO
    noteShipping,
    //SỐ ĐIỆN THOẠI KHÁCH HÀNG
    receiverContactPhone,
    //Giao thành công
    linkShippingSuccess,
    //GHI CHÚ ĐƠN HÀNG
    note,
    //LÝ DO HỦY ĐƠN
    cancelReason,
    //Thông tin đơn vị vận chuyển
    transportUnitInformation,
    //Ảnh giao hàng
    image,

    salesPerson,
  } = all;

  // Tab Tất cả
  const ALL_COLUMNS_TABLE = [
    index,
    createdAt,
    orderType,
    orderCode,
    receiverName,
    subTotal,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    status,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ duyệt đơn
  const WAITING_FOR_APPROVAL_ORDER_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    inStockStatus,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ duyệt tồn
  const WAITING_FOR_APPROVAL_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    inStockStatus,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ đủ tồn
  const WAITING_FOR_ENOUGH_STOCK_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    inStockStatus,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ thanh toán
  const WAITING_FOR_PAYMENT_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ đóng gói
  const WAITING_FOR_PACKAGING_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactAddress,
    internalNote,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ xuất kho
  const WAITING_FOR_EXPORTING_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactAddress,
    shippingType,
    internalNote,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ lắp đặt
  const WAITING_FOR_ASSEMBLY_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactAddress,
    shippingType,
    internalNote,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ điều phối
  const WAITING_FOR_SHIPPING_COLUMNS_TABLE_NOT_SET = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactPhone,
    receiverContactAddress,
    shippingType,
    paymentMethod,
    paymentStatus,
    internalNote,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ giao hàng
  const WAITING_FOR_SHIPPING_COLUMNS_TABLE_WAITING = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactPhone,
    receiverContactAddress,
    shippingType,
    transportUnitInformation,
    paymentMethod,
    paymentStatus,
    internalNote,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Đang giao hàng
  const DELIVERING_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactPhone,
    receiverContactAddress,
    shippingType,
    transportUnitInformation,
    paymentMethod,
    paymentStatus,
    internalNote,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Hoàn thành
  const COMPLETED_COLUMNS_TABLE = [
    index,
    createdAt,
    orderType,
    orderCode,
    receiverName,
    subTotal,
    exportedWarehouse,
    shippingType,
    transportUnitInformation,
    paymentMethod,
    image,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Đã hủy
  const CANCELED_COLUMNS_TABLE = [
    index,
    createdAt,
    orderType,
    orderCode,
    receiverName,
    subTotal,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    cancelReason,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  const TABLE_BY_TAB = {
    undefined: ALL_COLUMNS_TABLE,
    [`${STATUS_ORDER.ALL}`]: ALL_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_APPROVAL_ORDER}`]: WAITING_FOR_APPROVAL_ORDER_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_APPROVAL_STOCK}`]: WAITING_FOR_APPROVAL_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_ENOUGH_STOCK}`]: WAITING_FOR_ENOUGH_STOCK_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_PAYMENT}`]: WAITING_FOR_PAYMENT_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_PACKAGING}`]: WAITING_FOR_PACKAGING_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_EXPORTING}`]: WAITING_FOR_EXPORTING_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_ASSEMBLY}`]: WAITING_FOR_ASSEMBLY_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_SHIPPING_NOT_SET}`]: WAITING_FOR_SHIPPING_COLUMNS_TABLE_NOT_SET,
    [`${STATUS_ORDER.WAITING_FOR_SHIPPING_WAITING}`]: WAITING_FOR_SHIPPING_COLUMNS_TABLE_WAITING,
    [`${STATUS_ORDER.DELIVERING}`]: DELIVERING_COLUMNS_TABLE,
    [`${STATUS_ORDER.COMPLETED}`]: COMPLETED_COLUMNS_TABLE,
    [`${STATUS_ORDER.CANCELED}`]: CANCELED_COLUMNS_TABLE,
  };

  return { TABLE_BY_TAB, renderOrderPriority, renderPaymentMethod };
};

export default useColumnsOrder;
